import { useState, useEffect } from 'react';
import { useParams, Link, Navigate } from 'react-router-dom';
import axios from 'axios';
import { useAuth } from '@/contexts/AuthContext';
import { Checkbox } from '@/components/ui/checkbox';
import { Skeleton } from '@/components/ui/skeleton';
import { toast } from 'sonner';
import { Zap, Check, Calendar, User, Send, AlertCircle, ArrowRight, Download } from 'lucide-react';

import ExportPdfDialog from '@/components/ExportPdfDialog';
import { BLUE, BLUE_D, INK, MUTE, FAINT, LINE, CREAM, CARD_ALT, GREEN, SERIF, WORDMARK_BLUE } from '@/styles/milk';

const API_BASE = `${process.env.REACT_APP_BACKEND_URL}/api`;

function StatusPill({ status }) {
  const c = {
    in_progress: { label: 'In progress', bg: 'rgba(103,150,194,0.12)', color: BLUE_D },
    done: { label: 'Completed', bg: 'rgba(54,156,98,.12)', color: GREEN },
  }[status] || { label: status, bg: 'rgba(33,46,64,.06)', color: MUTE };
  return (
    <span className="text-[10px] font-semibold px-2 py-0.5 rounded-full" style={{ background: c.bg, color: c.color }}>
      {c.label}
    </span>
  );
}

function MessageBubble({ r }) {
  const owner = r.is_owner;
  const initial = (r.sender_name || '?').slice(0, 1).toUpperCase();
  return (
    <div className="flex items-start gap-2.5" data-testid="thread-message">
      <div className="w-7 h-7 rounded-full flex items-center justify-center text-[11px] font-bold shrink-0"
        style={owner ? { background: BLUE, color: '#fff' } : { background: CARD_ALT, color: MUTE, border: `1px solid ${LINE}` }}>
        {initial}
      </div>
      <div className="min-w-0 flex-1">
        <div className="flex items-center gap-2 flex-wrap">
          <span className="text-xs font-semibold" style={{ color: INK }}>{r.sender_name}{owner ? ' · owner' : ''}</span>
          <span className="text-[10px]" style={{ color: FAINT }}>
            {new Date(r.timestamp).toLocaleString('en-US', { month: 'short', day: 'numeric', hour: '2-digit', minute: '2-digit' })}
          </span>
        </div>
        <div className="text-sm mt-1 rounded-xl px-3 py-2 whitespace-pre-wrap"
          style={{ background: owner ? 'rgba(103,150,194,0.08)' : CARD_ALT, color: INK, border: `1px solid ${LINE}` }}>
          {r.message}
        </div>
      </div>
    </div>
  );
}

export default function SharedActionCardPage() {
  const { token } = useParams();
  const { user, loading: authLoading } = useAuth();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [tasks, setTasks] = useState([]);
  const [pdfOpen, setPdfOpen] = useState(false);
  const [completing, setCompleting] = useState({});
  const [senderName, setSenderName] = useState('');
  const [responseText, setResponseText] = useState('');
  const [sending, setSending] = useState(false);
  const [responses, setResponses] = useState([]);

  useEffect(() => {
    const meta = document.createElement('meta');
    meta.name = 'robots';
    meta.content = 'noindex, nofollow';
    document.head.appendChild(meta);
    document.title = 'Shared Action Card — Milk';

    // Logged-in users are redirected to the in-app view (below); skip the public fetch for them.
    if (authLoading || user) {
      return () => { try { document.head.removeChild(meta); } catch (_) {} };
    }
    // Record view for logged-in users (cookie sent automatically if present)
    axios.post(`${API_BASE}/a/${token}/viewed`).catch(() => {});

    (async () => {
      try {
        const res = await axios.get(`${API_BASE}/a/${token}`);
        setData(res.data);
        setTasks(res.data.card.tasks || []);
        setResponses(res.data.card.responses || []);
      } catch (err) {
        setError(err?.response?.data?.detail || 'This link is invalid or has been revoked');
      } finally {
        setLoading(false);
      }
    })();

    return () => { try { document.head.removeChild(meta); } catch (_) {} };
  }, [token, authLoading, user]);

  // A registered/logged-in user who opens a public share link gets the richer in-app view.
  if (!authLoading && user) {
    return <Navigate to={`/shared/${token}`} replace />;
  }

  const handleComplete = async (taskId, currentStatus) => {
    if (!data?.share?.permissions?.can_act) return;
    const newStatus = currentStatus === 'done' ? 'not_started' : 'done';
    setCompleting(p => ({ ...p, [taskId]: true }));
    try {
      const res = await axios.put(`${API_BASE}/a/${token}/tasks/${taskId}`, {
        status: newStatus,
        completed_by: senderName || 'External user',
      });
      setTasks(prev => prev.map(t => t.id === taskId ? res.data : t));
      if (newStatus === 'done') toast.success('Task marked complete!');
    } catch (err) {
      console.error('Task update failed:', err);
      toast.error('Failed to update task');
    } finally {
      setCompleting(p => ({ ...p, [taskId]: false }));
    }
  };

  const handleSendResponse = async (e) => {
    e.preventDefault();
    if (!responseText.trim()) return;
    setSending(true);
    try {
      await axios.post(`${API_BASE}/a/${token}/response`, {
        message: responseText,
        sender_name: senderName || 'External user',
      });
      setResponses(prev => [...prev, {
        id: `local-${Date.now()}`,
        sender_name: senderName || 'External user',
        message: responseText,
        is_owner: false,
        timestamp: new Date().toISOString(),
      }]);
      setResponseText('');
      toast.success('Response sent!');
    } catch (err) {
      console.error('Response send failed:', err);
      toast.error('Failed to send response');
    } finally {
      setSending(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen" style={{ background: CREAM }}>
        <div className="max-w-5xl mx-auto px-5 sm:px-8 py-10 space-y-5">
          <Skeleton className="h-8 w-48" style={{ background: 'rgba(33,46,64,.06)' }} />
          <Skeleton className="h-32 w-full rounded-2xl" style={{ background: 'rgba(33,46,64,.06)' }} />
          <Skeleton className="h-48 w-full rounded-2xl" style={{ background: 'rgba(33,46,64,.06)' }} />
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen flex items-center justify-center" style={{ background: CREAM, fontFamily: "'Work Sans', sans-serif" }}>
        <div className="text-center max-w-sm px-5">
          <div className="w-14 h-14 rounded-2xl flex items-center justify-center mx-auto mb-4" style={{ background: 'rgba(180,51,46,.08)', border: '1px solid rgba(180,51,46,.2)' }}>
            <AlertCircle size={22} style={{ color: '#B4332E' }} />
          </div>
          <h1 style={{ fontFamily: SERIF, fontSize: '28px', color: INK, lineHeight: 1.1 }} className="mb-2">Link not found</h1>
          <p className="text-sm mb-5" style={{ color: MUTE }}>{error}</p>
          <Link to="/">
            <button className="text-sm font-semibold px-5 py-2.5 rounded-full text-white" style={{ background: BLUE, boxShadow: '0 6px 16px rgba(103,150,194,.3)' }}>
              Open Milk
            </button>
          </Link>
        </div>
      </div>
    );
  }

  const { card, share } = data;
  const canAct = share?.permissions?.can_act;
  const pendingTasks = tasks.filter(t => t.status !== 'done');
  const doneTasks = tasks.filter(t => t.status === 'done');

  const cardStyle = { background: '#FFFFFF', border: `1px solid ${LINE}`, boxShadow: '0 1px 2px rgba(33,46,64,.04)' };

  return (
    <div className="min-h-screen" style={{ background: CREAM, fontFamily: "'Work Sans', sans-serif" }} data-testid="shared-action-card-page">
      {/* Header */}
      <header className="sticky top-0 z-10" style={{ borderBottom: `1px solid ${LINE}`, background: 'rgba(244,242,235,.85)', backdropFilter: 'blur(12px)' }}>
        <div className="max-w-5xl mx-auto px-5 sm:px-8 py-4 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <img src={WORDMARK_BLUE} alt="Milk" style={{ height: '24px', width: 'auto', display: 'block' }} />
            <span className="text-[11px] hidden sm:inline" style={{ color: FAINT }}>
              Shared by <span className="font-semibold" style={{ color: MUTE }}>{share?.shared_by_name}</span>
            </span>
          </div>
          <Link to="/register">
            <button
              className="text-xs font-semibold px-3.5 py-2 rounded-full flex items-center gap-1.5"
              style={{ background: '#FFFFFF', border: `1px solid ${LINE}`, color: BLUE_D, transition: 'border-color .15s' }}
              data-testid="shared-card-join-button"
              onMouseEnter={e => { e.currentTarget.style.borderColor = BLUE; }}
              onMouseLeave={e => { e.currentTarget.style.borderColor = LINE; }}
            >
              Join Milk free <ArrowRight size={12} />
            </button>
          </Link>
        </div>
      </header>

      {/* Watermark */}
      <div className="max-w-5xl mx-auto px-5 sm:px-8 pt-4">
        <div className="flex items-center gap-2 text-xs py-1.5 px-3 rounded-full w-fit" style={{ background: 'rgba(103,150,194,.08)', color: BLUE_D }}>
          <span className="w-1.5 h-1.5 rounded-full inline-block" style={{ background: BLUE }} />
          Private link — do not share further
        </div>
      </div>

      <main className="max-w-5xl mx-auto px-5 sm:px-8 py-5 sm:py-7 space-y-4">
        <ExportPdfDialog open={pdfOpen} onOpenChange={setPdfOpen} card={card} tasks={tasks} />
        {/* Card Header */}
        <div className="rounded-2xl p-6" style={cardStyle}>
          <h1 style={{ fontFamily: SERIF, fontSize: 'clamp(30px,5vw,44px)', lineHeight: 1.04, letterSpacing: '-0.3px', color: INK }} className="mb-2">
            {card.contact_name}
          </h1>
          <div className="flex items-center gap-2 flex-wrap text-xs" style={{ color: FAINT }}>
            <span>
              {new Date(card.created_at).toLocaleDateString('en-US', { month: 'long', day: 'numeric', year: 'numeric' })}
            </span>
            {card.tags?.map(tag => (
              <span key={tag} className="px-2 py-0.5 rounded-full text-[10px] font-medium" style={{ background: 'rgba(103,150,194,0.10)', color: BLUE_D }}>
                {tag}
              </span>
            ))}
            <button
              data-testid="shared-public-export-pdf"
              onClick={() => setPdfOpen(true)}
              className="ml-auto inline-flex items-center gap-1.5 px-2.5 py-1 rounded-lg border text-[11px] font-medium transition-colors"
              style={{ color: BLUE_D, borderColor: 'rgba(103,150,194,0.35)' }}
            >
              <Download size={12} />Export PDF
            </button>
          </div>
          {tasks.length > 0 && (
            <div className="mt-5">
              <div className="flex items-center justify-between text-xs mb-1.5" style={{ color: MUTE }}>
                <span>{doneTasks.length} of {tasks.length} tasks complete</span>
                <span className="font-semibold" style={{ color: INK }}>{Math.round((doneTasks.length / tasks.length) * 100)}%</span>
              </div>
              <div className="h-1.5 rounded-full overflow-hidden" style={{ background: 'rgba(33,46,64,.08)' }}>
                <div className="h-full rounded-full transition-all duration-500" style={{ width: `${(doneTasks.length / tasks.length) * 100}%`, background: BLUE }} />
              </div>
            </div>
          )}
        </div>

        <div className="grid lg:grid-cols-2 gap-4 lg:gap-5 items-start">
        <div className="space-y-4">
        {/* Summary */}
        {card.summary_bullets?.length > 0 && (
          <div className="rounded-2xl p-5" style={cardStyle}>
            <h2 className="text-xs font-bold uppercase tracking-wider mb-3 flex items-center gap-2" style={{ color: FAINT, letterSpacing: '0.1em' }}>
              <Zap size={13} style={{ color: BLUE }} fill="currentColor" />Summary
            </h2>
            <ul className="space-y-2.5">
              {card.summary_bullets.map((bullet, i) => (
                <li key={i} className="flex items-start gap-2.5 text-sm leading-relaxed" style={{ color: MUTE }}>
                  <span className="mt-1.5 w-1.5 h-1.5 rounded-full shrink-0" style={{ background: BLUE }} />
                  {bullet}
                </li>
              ))}
            </ul>
          </div>
        )}

        {/* Decisions */}
        {card.decisions?.length > 0 && (
          <div className="rounded-2xl p-5" style={cardStyle}>
            <h2 className="text-xs font-bold uppercase tracking-wider mb-3 flex items-center gap-2" style={{ color: FAINT, letterSpacing: '0.1em' }}>
              <Check size={13} style={{ color: GREEN }} />Decisions
            </h2>
            <ul className="space-y-2.5">
              {card.decisions.map((d, i) => (
                <li key={i} className="flex items-start gap-2.5 text-sm leading-relaxed" style={{ color: MUTE }}>
                  <Check size={13} style={{ color: GREEN, marginTop: '2px', flexShrink: 0 }} />{d}
                </li>
              ))}
            </ul>
          </div>
        )}

        </div>
        <div className="space-y-4">
        {/* Action Items */}
        {tasks.length > 0 && (
          <div className="rounded-2xl p-5" style={cardStyle}>
            <h2 className="mb-4 flex items-center justify-between">
              <span className="text-xs font-bold uppercase tracking-wider flex items-center gap-2" style={{ color: FAINT, letterSpacing: '0.1em' }}>
                <Check size={13} style={{ color: BLUE }} />
                Action Items
              </span>
              <span className="text-[10px]" style={{ color: FAINT }}>
                {canAct ? 'Click checkbox to complete' : 'View only'}
              </span>
            </h2>
            <div className="space-y-2.5">
              {[...pendingTasks, ...doneTasks].map(task => (
                <div key={task.id} className="flex items-start gap-3 py-2.5 px-3 rounded-xl"
                  style={{
                    background: task.status === 'done' ? 'rgba(54,156,98,.05)' : CARD_ALT,
                    border: `1px solid ${LINE}`,
                  }}>
                  {canAct ? (
                    <Checkbox
                      checked={task.status === 'done'}
                      onCheckedChange={() => handleComplete(task.id, task.status)}
                      disabled={completing[task.id]}
                      className="mt-0.5 shrink-0"
                    />
                  ) : (
                    <div className="w-4 h-4 rounded border shrink-0 mt-0.5 flex items-center justify-center"
                      style={{ background: task.status === 'done' ? GREEN : 'transparent', borderColor: task.status === 'done' ? GREEN : LINE }}>
                      {task.status === 'done' && <Check size={10} color="white" />}
                    </div>
                  )}
                  <div className="flex-1 min-w-0">
                    <p className="text-sm leading-snug" style={{ color: task.status === 'done' ? FAINT : INK, textDecoration: task.status === 'done' ? 'line-through' : 'none' }}>
                      {task.title}
                    </p>
                    <div className="flex items-center gap-3 mt-1 flex-wrap">
                      {task.owner && <span className="text-[10px] flex items-center gap-1" style={{ color: FAINT }}><User size={9} />{task.owner}</span>}
                      {task.due_date && <span className="text-[10px] flex items-center gap-1" style={{ color: FAINT }}><Calendar size={9} />{task.due_date}</span>}
                      {task.priority && (
                        <span className="text-[10px] font-semibold" style={{
                          color: task.priority === 'high' ? '#B4332E' : task.priority === 'medium' ? '#B8860B' : FAINT,
                        }}>{task.priority}</span>
                      )}
                    </div>
                  </div>
                  <StatusPill status={task.status} />
                </div>
              ))}
            </div>

            {/* Name field for task completion */}
            {canAct && (
              <div className="mt-4 pt-3" style={{ borderTop: `1px solid ${LINE}` }}>
                <input
                  value={senderName}
                  onChange={e => setSenderName(e.target.value)}
                  placeholder="Your name (shown when completing tasks)"
                  className="w-full rounded-lg px-3 h-9 text-xs outline-none"
                  style={{ background: CARD_ALT, border: `1px solid ${LINE}`, color: INK }}
                />
              </div>
            )}
          </div>
        )}

        {/* Conversation thread */}
        {(canAct || responses.length > 0) && (
          <div className="rounded-2xl p-5" style={cardStyle} data-testid="shared-thread">
            <h2 className="text-sm font-semibold mb-3" style={{ color: INK }}>Conversation</h2>

            {responses.length > 0 ? (
              <div className="space-y-3.5 mb-4">
                {responses.map(r => <MessageBubble key={r.id} r={r} />)}
              </div>
            ) : (
              <p className="text-xs mb-4" style={{ color: FAINT }}>
                No messages yet.{canAct ? ' Start the conversation below.' : ''}
              </p>
            )}

            {canAct && (
              <form onSubmit={handleSendResponse} className="space-y-3 pt-1" style={responses.length > 0 ? { borderTop: `1px solid ${LINE}`, paddingTop: 14 } : {}}>
                <textarea
                  value={responseText}
                  onChange={e => setResponseText(e.target.value)}
                  placeholder="Add a note, question, or update…"
                  className="w-full rounded-xl px-3 py-2.5 text-sm resize-none outline-none"
                  style={{ background: CARD_ALT, border: `1px solid ${LINE}`, color: INK }}
                  rows={3}
                  required
                />
                <button type="submit" disabled={sending || !responseText.trim()}
                  data-testid="shared-thread-send"
                  className="flex items-center gap-1.5 text-sm font-semibold px-4 py-2.5 rounded-full text-white disabled:opacity-50"
                  style={{ background: BLUE, boxShadow: '0 6px 16px rgba(103,150,194,.3)' }}>
                  {sending ? 'Sending…' : <><Send size={13} />{responses.length > 0 ? 'Reply' : 'Send response'}</>}
                </button>
              </form>
            )}
          </div>
        )}
        </div>
        </div>
      </main>

      <footer className="max-w-5xl mx-auto px-5 sm:px-8 py-10 text-center">
        <p className="text-xs" style={{ color: FAINT }}>
          Powered by <Link to="/" className="font-semibold" style={{ color: BLUE_D }}>Milk</Link>
          {' '}— You talked. It's handled.
        </p>
      </footer>
    </div>
  );
}
